/**
 * Settings Center — frontend demo state (F15).
 * Shape mirrors the intended backend `site_settings` key/value contract.
 * No credential is persisted here; secret fields are always masked placeholders.
 */

export type SettingsTabKey =
  | "general"
  | "brand"
  | "contact"
  | "social"
  | "email"
  | "sms"
  | "ai"
  | "telegram"
  | "analytics"
  | "seo"
  | "media"
  | "chat"
  | "page-builder"
  | "geo-routing"
  | "navatel"
  | "integrations"
  | "security"
  | "system";

export const SETTINGS_TABS: { key: SettingsTabKey; label: string }[] = [
  { key: "general", label: "عمومی" },
  { key: "brand", label: "برند" },
  { key: "contact", label: "تماس" },
  { key: "social", label: "شبکه‌های اجتماعی" },
  { key: "email", label: "ایمیل" },
  { key: "security", label: "امنیت" },
  { key: "system", label: "سیستم" },
  { key: "navatel", label: "تنظیمات نواتل" },
];

/**
 * F34.2 §19 — modules that own their own settings surface.
 * These tabs used to be duplicated inside generic Settings; the routes now
 * redirect to the canonical module screen so a single form exists per concern.
 */
export const DEPRECATED_SETTINGS_REDIRECTS: Record<string, string> = {
  sms: "/admin/sms",
  ai: "/admin/settings/ai",
  telegram: "/admin/chat",
  chat: "/admin/chat",
  media: "/admin/media/settings",
  seo: "/admin/seo/general",
  analytics: "/admin/analytics",
  "page-builder": "/admin/page-builder/settings",
  "geo-routing": "/admin/geo-routing/settings",
  navatel: "/admin/calls/settings",
  integrations: "/admin/settings/integrations",
  calls: "/admin/calls/settings",
  bookings: "/admin/bookings",
  payments: "/admin/payments",
  backups: "/admin/backups/settings",
};

export const NOTIFICATION_TRIGGERS = [
  { key: "lead", label: "لید جدید" },
  { key: "form", label: "ثبت فرم جدید" },
  { key: "opportunity", label: "فرصت فروش جدید" },
  { key: "application", label: "رزومه جدید" },
  { key: "conversation", label: "مکالمه گفتگوی جدید" },
] as const;

export const AI_PROVIDERS = [
  { key: "openai", label: "OpenAI", models: ["gpt-5.4-mini", "gpt-5.4", "gpt-5.5"] },
  { key: "anthropic", label: "Anthropic Claude", models: ["claude-sonnet", "claude-opus"] },
  { key: "gemini", label: "Google Gemini", models: ["gemini-3.6-flash", "gemini-3.1-pro"] },
] as const;

export const SMS_PROVIDERS = ["کاوه‌نگار", "فراز اس‌ام‌اس", "ملی‌پیامک", "SMS.ir"];

export const TIMEZONES = ["Asia/Tehran", "UTC", "Europe/Berlin", "Asia/Dubai"];
export const DATE_FORMATS = [
  "۱۴۰۳/۰۵/۱۲ (جلالی)",
  "1403-05-12 (جلالی عددی)",
  "2024-08-02 (میلادی)",
];
export const PAGE_SIZES = [10, 20, 25, 50, 100];

export type SettingsState = Record<string, string | boolean | number>;

export const SETTINGS_DEFAULTS: SettingsState = {
  // general
  site_name: "آژانس ماه",
  site_tagline: "مسیر رشد را روشن می‌کنیم",
  admin_title: "پنل مدیریت ماه",
  timezone: "Asia/Tehran",
  locale: "fa-IR",
  calendar: "jalali",
  date_format: DATE_FORMATS[0],
  page_size: 25,
  admin_density: "compact",
  // brand
  brand_primary: "#0B1F3B",
  brand_accent: "#4FA3F7",
  brand_logo: "/brand/mah-logo.svg",
  brand_favicon: "/favicon.ico",
  // contact
  phone_primary: "۰۲۱-۹۱۳۰۰۹۳۹",
  phone_secondary: "۰۹۹۰۰۰۰۰۰۰۰",
  email_public: "hello@mah.agency",
  office_tehran: "تهران، خیابان ولیعصر",
  office_isfahan: "اصفهان، خیابان شیخ صدوق",
  social_linkedin: "https://www.linkedin.com/company/mah-agency/",
  social_instagram: "https://instagram.com/mah_agency",
  social_telegram: "https://t.me/mah_agency",
  social_bale: "https://ble.ir/mahagency/",
  social_github: "https://github.com/pendar-naseri",
  // email
  email_sender_name: "آژانس ماه",
  email_sender: "notify@notify.mail.mah.agency",
  email_reply_to: "hello@mah.agency",
  email_enabled: true,
  // sms
  sms_enabled: false,
  sms_provider: SMS_PROVIDERS[0],
  sms_sender: "۳۰۰۰۱۲۳۴",
  // ai
  ai_alt_text: true,
  ai_content_assist: false,
  ai_seo_assist: false,
  // telegram
  tg_enabled: false,
  tg_notify_new_conversation: true,
  tg_notify_new_message: true,
  tg_allow_replies: false,
  tg_target_chat: "-1001234567890",
  // analytics
  analytics_enabled: true,
  analytics_anonymize_ip: true,
  analytics_retention: "۱۲ ماه",
  analytics_consent: true,
  analytics_exclude_internal: true,
  // seo
  seo_indexing: true,
  seo_title_template: "%title% | آژانس ماه",
  seo_default_og: "/og/default.jpg",
  // media
  media_max_size: "۵ مگابایت",
  media_webp: true,
  media_thumbnails: true,
  // chat
  chat_enabled: true,
  chat_welcome: "سلام! چطور می‌توانیم به رشد کسب‌وکار شما کمک کنیم؟",
  chat_offline_message: "الان آفلاین هستیم؛ پیام بگذارید تا تماس بگیریم.",
  chat_identity_form: true,
  chat_require_name: true,
  chat_require_phone: true,
  chat_require_email: false,
  chat_hours: "شنبه تا چهارشنبه، ۹ تا ۱۸",
  chat_visibility: "all",
  // security
  sec_session_hours: "۸ ساعت",
  sec_2fa: false,
  sec_password_policy: "حداقل ۱۰ کاراکتر، حروف و عدد",
  sec_audit_log: true,
  sec_allowed_origins: "https://mah.agency",
};
